import { FC, ReactNode, useContext, useEffect, useState } from 'react';

import { Button, Checkbox } from '@selina-finance/ui';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { ApplicationContext } from '@App/contexts/Application';
import { AuthContext } from '@App/contexts/Auth';
import { intercomTrackRejectPageEvent } from '@App/intercom/intercom';
import { saveResaleConsent } from '@App/requests/saveApplicationData';
import CheckIcon from '@src/ui/components/Icons/CheckIcon';
import LoanCoUKLogoIcon from '@src/ui/components/Icons/LoanCoUKLogoIcon';
import LoanPartnershipLogoIcon from '@src/ui/components/Icons/LoanPartnershipLogoIcon';

import Help from '../../components/Help/Help';

type ConsentStatus = 'pending' | 'opted-in' | 'opted-out';

interface CardContent {
    title: string;
    description: ReactNode;
}

const getCardContent = (status: ConsentStatus): CardContent => {
    if (status === 'opted-in') {
        return {
            title: 'Thanks, we will share your details',
            description: (
                <>
                    <p>
                        We will share your name, email address, and phone number
                        with one of our broker partners below.
                        <br />
                        <br />
                        They may contact you about other borrowing options, but
                        until then, <b>you don't need to do anything else</b>.
                    </p>
                </>
            ),
        };
    }
    if (status === 'opted-out') {
        return {
            title: "No problem, we won't share your details",
            description: (
                <>
                    <p>
                        Your name, email address, and phone number{' '}
                        <b>won't be shared</b>.
                        <br />
                        <br />
                        If your circumstances change, you can return to us at
                        any time to check your eligibility again.
                    </p>
                </>
            ),
        };
    }
    return {
        title: 'What you can do next',
        description:
            "If you'd like, and with your explicit consent, we can share your name, phone number and email with one of our broker partners below. They may contact you by phone or email about other borrowing options.",
    };
};

const getFooterContent = (status: ConsentStatus): ReactNode => {
    const privacyPolicyLink = (
        <Link
            className='underline'
            to='https://www.selinafinance.co.uk/privacy-policy'
        >
            Privacy Policy
        </Link>
    );

    if (status === 'opted-in') {
        return (
            <p>
                You can withdraw your consent at any time before your
                information is shared, see our {privacyPolicyLink} for more
                details.
                <br />
                <br />
                For more information on how our broker partners handle your data
                and how to opt out once your information is shared, view their
                privacy policies here:{' '}
                <Link
                    className='underline'
                    to='https://www.clearscore.com/privacy-policy'
                >
                    ClearScore
                </Link>
                ,{' '}
                <Link
                    className='underline'
                    to='https://www.loan.co.uk/privacy-policy'
                >
                    Loan.co.uk
                </Link>
                ,{' '}
                <Link
                    className='underline'
                    to='https://www.theloanpartnership.co.uk/privacy-policy'
                >
                    The Loan Partnership
                </Link>
                .
            </p>
        );
    }

    if (status === 'opted-out') {
        return (
            <p>
                Read our {privacyPolicyLink} to understand how we use and
                protect your data.
            </p>
        );
    }

    return (
        <p>
            Selina will receive a fee for referring you to one of our broker
            partners. Read our {privacyPolicyLink} to understand how we use and
            protect your data.
        </p>
    );
};

const getConsentStorageKey = (applicationId: string) =>
    `resaleConsent_${applicationId}`;

const RejectView: FC<{ className?: string }> = (props) => {
    const { intercomData, applicationId } = useContext(ApplicationContext);
    const { getAccessToken, getIdToken } = useContext(AuthContext);

    const [isChecked, setIsChecked] = useState(false);
    const [hasCheckboxError, setHasCheckboxError] = useState(false);
    const [consentStatus, setConsentStatus] =
        useState<ConsentStatus>('pending');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => intercomTrackRejectPageEvent(intercomData), [intercomData]);

    useEffect(() => {
        if (!applicationId) return;
        const stored = sessionStorage.getItem(
            getConsentStorageKey(applicationId),
        );
        if (stored === 'opted-in' || stored === 'opted-out') {
            setConsentStatus(stored);
        }
    }, [applicationId]);

    useEffect(() => {
        if (!applicationId || consentStatus === 'pending') return;
        sessionStorage.setItem(
            getConsentStorageKey(applicationId),
            consentStatus,
        );
    }, [consentStatus, applicationId]);

    const cardContent = getCardContent(consentStatus);
    const footerContent = getFooterContent(consentStatus);

    const handleContinue = async () => {
        if (!isChecked) {
            setHasCheckboxError(true);
            return;
        }

        setIsLoading(true);
        try {
            const timestamp = new Date().toISOString();
            const response = await saveResaleConsent(
                applicationId,
                await getAccessToken(),
                await getIdToken(),
                true,
                timestamp,
            );

            if (response.success) {
                setConsentStatus('opted-in');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleNoThanks = async () => {
        setIsLoading(true);
        try {
            const timestamp = new Date().toISOString();
            const response = await saveResaleConsent(
                applicationId,
                await getAccessToken(),
                await getIdToken(),
                false,
                timestamp,
            );

            if (response.success) {
                setConsentStatus('opted-out');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCheckboxChange = () => {
        setIsChecked((prev) => !prev);
        setHasCheckboxError(false);
    };

    return (
        <>
            <div
                className={clsx(
                    props.className,
                    'flex flex-col items-center justify-center md:max-w-600 mx-auto',
                )}
            >
                <div className='reject-view-container mb-8'>
                    <h2 className='text-44 text-midnight-600 font-sora-bold leading-12 tracking-tight-xl mb-5 lg:flex'>
                        Unfortunately, we can't offer you a loan right now
                    </h2>
                    <div className='grid gap-2 leading-snug'>
                        <p className='text-midnight-600 text-body font-normal leading-6'>
                            There are several possible reasons based on the
                            information you've provided, such as:{' '}
                            <b>
                                affordability, information on your credit file,
                                or property details
                            </b>
                            {'.'}
                            <br />
                            <br />
                            You can apply again in the future if your
                            circumstances change.
                        </p>
                    </div>
                </div>

                {/* Lead reselling Opt in */}
                <div className='flex flex-col gap-5 lg:gap-6 justify-center items-start w-full p-6 lg:p-8 rounded-bl-2xl rounded-br rounded-tl-2xl rounded-tr-2xl border-2 border-b-5 border-midnight-500 bg-almond-100'>
                    <div className='flex flex-col justify-start items-start gap-4'>
                        {consentStatus !== 'pending' &&
                            consentStatus !== 'opted-out' && (
                                <div className='flex justify-center items-center gap-2'>
                                    <CheckIcon />
                                </div>
                            )}
                        <h2 className='text-midnight-600 text-28 font-sora-bold leading-8 tracking-tight-lg'>
                            {cardContent.title}
                        </h2>
                        <p className='text-midnight-600 text-body font-normal leading-6'>
                            {cardContent.description}
                        </p>
                    </div>
                    {consentStatus !== 'opted-out' && (
                        <div className='flex justify-center items-center gap-12 w-full'>
                            <LoanCoUKLogoIcon />
                            <LoanPartnershipLogoIcon />
                        </div>
                    )}
                    {consentStatus === 'pending' && (
                        <>
                            <div className='flex flex-col gap-2'>
                                <Checkbox
                                    name='lead-reselling-opt-in'
                                    checked={isChecked}
                                    onChange={handleCheckboxChange}
                                    error={
                                        hasCheckboxError
                                            ? {
                                                  hasError: true,
                                                  errorMessage:
                                                      "Select the checkbox if you'd like to continue and for us to share your name, phone number and email",
                                              }
                                            : undefined
                                    }
                                >
                                    <p className='text-midnight-600 text-body font-normal leading-6'>
                                        I agree to Selina sharing my name, phone
                                        number and email for the above purpose.
                                    </p>
                                </Checkbox>
                            </div>
                            <div className='flex flex-col lg:flex-row justify-between items-center gap-3 w-full'>
                                <Button
                                    color='secondary-flat'
                                    className='w-full'
                                    size='medium'
                                    onClick={handleContinue}
                                    disabled={isLoading}
                                >
                                    Continue
                                </Button>
                                <Button
                                    color='tertiary-flat'
                                    className='w-full rounded-bl-2xl rounded-br rounded-tl-2xl rounded-tr-2xl'
                                    size='medium'
                                    onClick={handleNoThanks}
                                    disabled={isLoading}
                                >
                                    No, thanks
                                </Button>
                            </div>
                        </>
                    )}
                </div>
                <div className='text-midnight-600 text-sm font-normal mt-4 lg:mt-5 leading-18px text-center'>
                    {footerContent}
                </div>
            </div>
            <Help />
        </>
    );
};

export default RejectView;
